// =============================================================================
// components/KpiRules/EngineLog.jsx
//
// Detection engine panel, collapsed by default — shows only a one-line
// summary (last run) until the user expands it. When expanded, it has the
// manual trigger (compact, on a single line) and the history, limited to
// 5 entries with a "show more" option.
// =============================================================================
import { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, Button, TextField,
  CircularProgress, Alert, Divider, ToggleButton, ToggleButtonGroup, Tooltip,
  Collapse, IconButton,
} from '@mui/material';
import PlayArrowIcon    from '@mui/icons-material/PlayArrow';
import RefreshIcon      from '@mui/icons-material/Refresh';
import TimelineIcon     from '@mui/icons-material/Timeline';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import HistoryIcon      from '@mui/icons-material/History';
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore';
import CircleIcon       from '@mui/icons-material/Circle';

import { useKpiRules } from '../../hooks/useKpi';
import {
  apiRunBaseline,
  apiRunDetection,
  apiRunFullPipeline,
} from '../../requests/kpiRequests';

const STATUS_COLOR = {
  Running: 'info',
  Success: 'success',
  Error:   'error',
};

const MODES = {
  baseline: {
    label: 'Baseline',
    icon: <TimelineIcon fontSize="small" />,
    req: apiRunBaseline,
    hint: 'Only updates baselines (without detecting).',
  },
  detection: {
    label: 'Detection',
    icon: <TroubleshootIcon fontSize="small" />,
    req: apiRunDetection,
    hint: 'Only detects (assumes the baseline already exists).',
  },
  full: {
    label: 'Pipeline',
    icon: <AllInclusiveIcon fontSize="small" />,
    req: apiRunFullPipeline,
    hint: 'Baseline + Detection for the same date.',
  },
};

function yesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

const PAGE_SIZE = 5;

export default function EngineLog() {
  const { engineLogs, engineLogsLoading, loadLogs } = useKpiRules();

  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [mode, setMode]       = useState('detection');
  const [date, setDate]       = useState(yesterdayIso());
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const [runOk, setRunOk]     = useState(null);

  useEffect(() => { loadLogs(); }, []); // eslint-disable-line

  const handleModeChange = (_, newMode) => {
    if (!newMode) return;
    setMode(newMode);
    setRunOk(null);
    setRunError(null);
  };

  const handleRun = async () => {
    setRunning(true);
    setRunError(null);
    setRunOk(null);
    try {
      const res = await MODES[mode].req(date);
      setRunOk(res.data?.message || `"${MODES[mode].label}" run completed.`);
      loadLogs();
    } catch (err) {
      setRunError(err.response?.data?.error || err.message);
    } finally {
      setRunning(false);
    }
  };

  const lastLog = engineLogs[0];
  const visibleLogs = engineLogs.slice(0, visibleCount);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {/* ============================================ */}
      {/* Header — clickable, summarises current state */}
      {/* ============================================ */}
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
      >
        <HistoryIcon fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>
          Detection Engine
        </Typography>

        {!expanded && lastLog && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Last run: {new Date(lastLog.runDate).toLocaleDateString('en-GB')}
            </Typography>
            <Chip
              label={lastLog.status} size="small" variant="outlined"
              color={STATUS_COLOR[lastLog.status] || 'default'} sx={{ height: 20 }}
            />
          </>
        )}
        {!expanded && !lastLog && !engineLogsLoading && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            No runs recorded yet
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />
        <IconButton size="small">
          <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          {/* ============================================ */}
          {/* Manual run — compact, on a single line       */}
          {/* ============================================ */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
            <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small" color="primary">
              {Object.keys(MODES).map((key) => (
                <Tooltip key={key} title={MODES[key].hint}>
                  <ToggleButton value={key}>
                    {MODES[key].icon}&nbsp;{MODES[key].label}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>

            <TextField
              type="date" size="small"
              value={date} onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <Button
              variant="contained" size="small"
              startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleRun} disabled={running}
            >
              {running ? 'Running...' : 'Run'}
            </Button>

            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={loadLogs} disabled={engineLogsLoading}>
              Refresh
            </Button>
          </Stack>

          {runOk    && <Alert severity="success" sx={{ mt: 1.5, py: 0 }}>{runOk}</Alert>}
          {runError && <Alert severity="error"   sx={{ mt: 1.5, py: 0 }}>{runError}</Alert>}

          <Divider sx={{ my: 2 }} />

          {/* ============================================ */}
          {/* History — condensed, with "show more"        */}
          {/* ============================================ */}
          {engineLogsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {!engineLogsLoading && engineLogs.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No runs recorded yet.
            </Typography>
          )}

          <Stack spacing={0.5}>
            {visibleLogs.map((log) => (
              <Box
                key={log.logId}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
              >
                <CircleIcon sx={{ fontSize: 8, color: `${STATUS_COLOR[log.status] || 'grey'}.main`, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ width: 92, flexShrink: 0 }}>
                  {new Date(log.runDate).toLocaleDateString('en-GB')}
                </Typography>
                <Chip label={log.status} size="small" variant="outlined"
                  color={STATUS_COLOR[log.status] || 'default'} sx={{ height: 20, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
                  +{log.incidentsOpened} opened · ~{log.incidentsUpdated} kept · -{log.incidentsClosed} closed
                </Typography>
                {log.errorMessage && (
                  <Tooltip title={log.errorMessage}>
                    <Typography variant="caption" color="warning.main" sx={{ flexShrink: 0 }}>Warning</Typography>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Stack>

          {engineLogs.length > visibleCount && (
            <Button size="small" onClick={() => setVisibleCount((v) => v + PAGE_SIZE)} sx={{ mt: 1 }}>
              Show more
            </Button>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
